import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "reactstrap";
import moment from "moment";

import api from "../../../services/api";
import CanceladoSefazBadge from "../../../components/cte/CanceladoSefazBadge";
import VerXmlCteButton from "../../../components/cte/VerXmlCteButton";
import NotaFiscalDetailModal from "../ConsultaNotasFiscais/NotaFiscalDetailModal";
import { formatDocument } from "../../../utils/formatDoc";
import { TipoBadge } from "./componentes";

import "../ConsultaNotasFiscais/styles.css";
import "./styles.css";

// Detalhe somente leitura do CT-e (PO1-2968). Não usa o CteAuditDetailModal da
// Auditoria (tem recálculo e checks): aqui são só identificação, partes com o
// papel do tomador, valores, notas vinculadas e situação. "Ver XML" reaproveita
// o botão do detalhe da auditoria (lê o XML do banco).

const fmtCurrency = (value) =>
  value == null ? "-" : new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
const fmtDate = (value, withTime = false) => (value ? moment(value).format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY") : "-");
const fmtDateUtc = (value) => (value ? moment.utc(value).format("DD/MM/YYYY") : "-");
const location = (city, state) => [city, state].filter(Boolean).join("/") || "-";

const Field = ({ label, value, wide = false, mono = false }) => (
  <div className={`nfe-detail-field${wide ? " nfe-detail-field--wide" : ""}`}>
    <span>{label}</span>
    <strong className={mono ? "nfe-detail-mono" : ""}>{value == null || value === "" ? "-" : value}</strong>
  </div>
);

const Section = ({ eyebrow, title, children, className = "" }) => (
  <section className={`nfe-detail-section ${className}`}>
    <div className="nfe-detail-section__header">
      {eyebrow && <span>{eyebrow}</span>}
      <h4>{title}</h4>
    </div>
    {children}
  </section>
);

const OverviewTab = ({ summary, valores, situacao }) => (
  <div className="nfe-detail-overview-grid">
    <Section eyebrow="Documento" title="Identificação">
      <div className="nfe-detail-fields nfe-detail-fields--three">
        <Field label="Emissão" value={fmtDate(summary.emissao, true)} />
        <Field label="Importação" value={fmtDate(summary.importacao, true)} />
        <Field label="Previsão de entrega" value={fmtDate(summary.previsaoEntrega)} />
        <Field label="Tipo" value={summary.tipoLabel} />
        <Field label="Modal" value={summary.modal} />
        <Field label="Modelo" value={summary.modelo} />
        <Field label="CFOP" value={[summary.cfopCodigo, summary.cfopNome].filter(Boolean).join(" · ")} />
        <Field label="Natureza da operação" value={summary.naturezaOperacao} wide />
      </div>
    </Section>

    <Section eyebrow="Operação" title="Rota e transportadora">
      <div className="nfe-detail-route">
        <span>Trajeto do CT-e</span>
        <strong>
          {location(summary.cidadeInicio, summary.ufInicio)} → {location(summary.cidadeFim, summary.ufFim)}
        </strong>
      </div>
      <div className="nfe-detail-fields nfe-detail-fields--three">
        <Field label="Transportadora" value={summary.transportadora} />
        <Field label="CNPJ" value={formatDocument(summary.transportadoraCnpj)} mono />
        <Field label="Notas vinculadas" value={summary.quantidadeNotas} />
      </div>
    </Section>

    <Section eyebrow="Valores" title="Frete e carga" className="nfe-detail-section--full">
      <div className="nfe-detail-fields nfe-detail-fields--six">
        <Field label="Valor da mercadoria" value={fmtCurrency(valores.mercadoria)} />
        <Field label="Prestação do serviço" value={fmtCurrency(valores.prestacao)} />
        <Field label="Frete a receber" value={fmtCurrency(valores.freteReceber)} />
        <Field label="Frete simulado" value={fmtCurrency(valores.freteSimulado)} />
        <Field label="Tributos" value={fmtCurrency(valores.tributos)} />
        <Field label="Situação na fatura" value={situacao.situacaoFatura || "Sem fatura"} />
      </div>
    </Section>

    <Section eyebrow="Situação" title="Estado do documento" className="nfe-detail-section--full">
      <div className="nfe-detail-fields nfe-detail-fields--three">
        <Field label="SEFAZ" value={situacao.canceladoSefaz ? <CanceladoSefazBadge compact /> : "Sem cancelamento registrado"} />
        <Field label="Pago" value={situacao.pago ? "Sim" : "Não"} />
        <Field label="Divergência de frete" value={situacao.divergencia ? "Com divergência" : "Sem divergência"} />
      </div>
    </Section>
  </div>
);

const PARTES = [
  ["remetente", "Remetente"],
  ["destinatario", "Destinatário"],
  ["expedidor", "Expedidor"],
  ["recebedor", "Recebedor"],
];

const PartesTab = ({ partes }) => (
  <div className="nfe-detail-links">
    <Section eyebrow="Partes" title="Quem participa do transporte">
      <div className="nfe-detail-party-grid nfe-detail-party-grid--four">
        {PARTES.map(([chave, rotulo]) => {
          const parte = partes[chave] || {};
          return (
            <div key={chave} data-testid={`pcte-parte-${chave}`}>
              <span>{rotulo}</span>
              <strong>{parte.nome || "-"}</strong>
              <small>{parte.cnpj ? formatDocument(parte.cnpj) : "-"}</small>
            </div>
          );
        })}
      </div>
    </Section>
    <Section eyebrow="Tomador" title="Quem paga o frete">
      <div className="nfe-detail-fields nfe-detail-fields--three">
        <Field label="Tomador" value={partes.tomador && partes.tomador.nome} />
        <Field label="CNPJ/CPF" value={partes.tomador && partes.tomador.cnpj ? formatDocument(partes.tomador.cnpj) : null} mono />
        <Field
          label="Papel na operação"
          value={partes.tomador && partes.tomador.papelLabel ? <span className="pesquisa-doc-papel">{partes.tomador.papelLabel}</span> : null}
        />
      </div>
    </Section>
  </div>
);

const NotasTab = ({ notas, onAbrirNota }) => (
  <div className="nfe-detail-links">
    <Section eyebrow="Documentos" title="Notas fiscais vinculadas">
      {!notas.length ? (
        <div className="nfe-detail-empty">Este CT-e não tem nota fiscal vinculada no Hub.</div>
      ) : (
        <div className="nfe-detail-table-wrap">
          <table className="nfe-detail-table hub-thead-accent">
            <thead>
              <tr>
                <th>NF</th>
                <th>Chave</th>
                <th>Emissão</th>
                <th>Destinatário</th>
                <th className="is-number">Valor</th>
              </tr>
            </thead>
            <tbody>
              {notas.map((nf) => (
                <tr key={nf.id}>
                  <td>
                    <button type="button" className="pesquisa-doc-link-nf" onClick={() => onAbrirNota(nf.id)} title="Abrir o detalhe da NF">
                      {nf.numero} / {nf.serie}
                    </button>
                  </td>
                  <td><small className="nfe-detail-mono">{nf.chave || "-"}</small></td>
                  <td>{fmtDateUtc(nf.emissao)}</td>
                  <td>{nf.destinatario || "-"}</td>
                  <td className="is-number">{fmtCurrency(nf.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  </div>
);

const PesquisaCteDetailModal = ({ isOpen, cteId, embarcadorId, transportadoraId, onClose }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notaId, setNotaId] = useState(null);

  useEffect(() => {
    if (!isOpen || !cteId) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    setData(null);
    setActiveTab("overview");
    api
      .get(`/pesquisa/ctes/${cteId}`, {
        params: { embarcadorId: embarcadorId || undefined, transportadoraId: transportadoraId || undefined },
      })
      .then((response) => {
        if (active) setData(response.data);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(
          (requestError.response && requestError.response.data && requestError.response.data.error) ||
            "Não foi possível carregar o detalhe do CT-e."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cteId, embarcadorId, isOpen, transportadoraId]);

  const summary = data && data.summary;

  const copyKey = () => {
    if (!summary || !summary.chave || !navigator.clipboard) return;
    navigator.clipboard.writeText(summary.chave).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" modalClassName="modal-align-top nfe-detail-modal-wrap" contentClassName="nfe-detail-modal" fade={false}>
      <div className="nfe-detail-header">
        <div>
          <span>Detalhe do CT-e</span>
          <h3>{summary ? `${summary.numero} / ${summary.serie}` : "Carregando documento"}</h3>
        </div>
        <div className="nfe-detail-header__actions">
          {summary && <TipoBadge item={summary} />}
          {summary && summary.canceladoSefaz && <CanceladoSefazBadge compact />}
          {/* Em <span>: a regra "> button" do cabeçalho fixa 34px (botão de fechar) e cortava o "Ver XML". */}
          {summary && <span><VerXmlCteButton summary={{ id: summary.id, numero: summary.numero }} /></span>}
          <button type="button" onClick={onClose} title="Fechar" aria-label="Fechar">
            ×
          </button>
        </div>
      </div>

      {summary && (
        <div className="nfe-detail-keybar">
          <div>
            <span>Chave de acesso</span>
            <strong className="nfe-detail-mono">{summary.chave || "-"}</strong>
          </div>
          <Button type="button" color="default" onClick={copyKey} disabled={!summary.chave}>
            <i className="tim-icons icon-single-copy-04" /> {copied ? "Copiada" : "Copiar"}
          </Button>
        </div>
      )}

      <div className="nfe-detail-body">
        {loading && (
          <div className="nfe-detail-state">
            <Spinner color="info" /> Carregando detalhe...
          </div>
        )}
        {!loading && error && <div className="nfe-detail-state nfe-detail-state--error">{String(error)}</div>}
        {!loading && !error && data && summary && (
          <>
            <div className="nfe-detail-tabs" role="tablist" aria-label="Seções do detalhe do CT-e">
              {[
                ["overview", "Visão geral"],
                ["partes", "Partes"],
                ["notas", `Notas vinculadas (${(data.notas || []).length})`],
              ].map(([id, label]) => (
                <button key={id} type="button" className={activeTab === id ? "is-active" : ""} onClick={() => setActiveTab(id)} role="tab" aria-selected={activeTab === id}>
                  {label}
                </button>
              ))}
            </div>
            {activeTab === "overview" && <OverviewTab summary={summary} valores={data.valores || {}} situacao={data.situacao || {}} />}
            {activeTab === "partes" && <PartesTab partes={data.partes || {}} />}
            {activeTab === "notas" && <NotasTab notas={data.notas || []} onAbrirNota={setNotaId} />}
          </>
        )}
      </div>

      {/* PO1-3842: mesmas abas da Consulta NFe, inclusive Ocorrências. */}
      <NotaFiscalDetailModal isOpen={Boolean(notaId)} notaFiscalId={notaId} embarcadorId={embarcadorId} transportadoraId={transportadoraId} onClose={() => setNotaId(null)} mostrarOcorrencias />
    </Modal>
  );
};

export default PesquisaCteDetailModal;
